/* teamThemes — the ONE authoritative team-colour resolver used by every sportsbook matchup surface
   (TeamLogo monogram, EventScoreboard wash, DiscoveryGameCard, BroadcastGameCard, FeaturedEventHero).

   Known major professional teams resolve to their real, canonical brand colours — never an arbitrary
   hash colour. Unknown / long-tail teams fall back to a deterministic palette (stable per name) so they
   still get a defined, on-brand chip. Resolution order (per spec §14):
     1. provider-supplied colour (if given + reliable)
     2. canonical team mapping (by league group + name identity)
     3. deterministic fallback palette

   Team identity reuses the SAME normalisation + league grouping as the logo map (constants/teamLogos.js)
   so "KC Chiefs" / "Kansas City Chiefs" / "Chiefs" all resolve identically, and the Cardinals in the NFL
   group never collide with the Cardinals in the MLB group. */

import { normTeam, groupFor } from "./teamLogos";

// Each entry: { m: [unique name substrings within the league], p: primary, s: secondary }.
// Match is "normalized input contains one of m" — so mascot tokens (e.g. "chiefs", "red sox") are enough,
// and shared cities (NY, LA, Chicago) are disambiguated by mascot.
const THEMES = {
  nfl: [
    { m: ["cardinals"], p: "#97233F", s: "#000000" },
    { m: ["falcons"], p: "#A71930", s: "#000000" },
    { m: ["ravens"], p: "#241773", s: "#9E7C0C" },
    { m: ["bills"], p: "#00338D", s: "#C60C30" },
    { m: ["panthers"], p: "#0085CA", s: "#101820" },
    { m: ["bears"], p: "#0B162A", s: "#C83803" },
    { m: ["bengals"], p: "#FB4F14", s: "#000000" },
    { m: ["browns"], p: "#311D00", s: "#FF3C00" },
    { m: ["cowboys"], p: "#003594", s: "#869397" },
    { m: ["broncos"], p: "#FB4F14", s: "#002244" },
    { m: ["lions"], p: "#0076B6", s: "#B0B7BC" },
    { m: ["packers"], p: "#203731", s: "#FFB612" },
    { m: ["texans"], p: "#03202F", s: "#A71930" },
    { m: ["colts"], p: "#003A70", s: "#A2AAAD" },
    { m: ["jaguars"], p: "#006778", s: "#9F792C" },
    { m: ["chiefs"], p: "#E31837", s: "#FFB81C" },
    { m: ["raiders"], p: "#000000", s: "#A5ACAF" },
    { m: ["chargers"], p: "#0080C6", s: "#FFC20E" },
    { m: ["rams"], p: "#003594", s: "#FFA300" },
    { m: ["dolphins"], p: "#008E97", s: "#FC4C02" },
    { m: ["vikings"], p: "#4F2683", s: "#FFC62F" },
    { m: ["patriots"], p: "#002244", s: "#C60C30" },
    { m: ["saints"], p: "#9F8958", s: "#101820" },
    { m: ["giants"], p: "#0B2265", s: "#A71930" },
    { m: ["jets"], p: "#125740", s: "#FFFFFF" },
    { m: ["eagles"], p: "#004C54", s: "#A5ACAF" },
    { m: ["steelers"], p: "#FFB612", s: "#101820" },
    { m: ["49ers", "niners"], p: "#AA0000", s: "#B3995D" },
    { m: ["seahawks"], p: "#002244", s: "#69BE28" },
    { m: ["buccaneers", "bucs"], p: "#D50A0A", s: "#34302B" },
    { m: ["titans"], p: "#0C2340", s: "#4B92DB" },
    { m: ["commanders"], p: "#5A1414", s: "#FFB612" },
  ],
  nba: [
    { m: ["hawks"], p: "#E03A3E", s: "#C1D32F" },
    { m: ["celtics"], p: "#007A33", s: "#BA9653" },
    { m: ["nets"], p: "#000000", s: "#FFFFFF" },
    { m: ["hornets"], p: "#1D1160", s: "#00788C" },
    { m: ["bulls"], p: "#CE1141", s: "#000000" },
    { m: ["cavaliers", "cavs"], p: "#860038", s: "#FDBB30" },
    { m: ["mavericks", "mavs"], p: "#00538C", s: "#002B5E" },
    { m: ["nuggets"], p: "#0E2240", s: "#FEC524" },
    { m: ["pistons"], p: "#C8102E", s: "#1D42BA" },
    { m: ["warriors"], p: "#1D428A", s: "#FFC72C" },
    { m: ["rockets"], p: "#CE1141", s: "#C4CED4" },
    { m: ["pacers"], p: "#002D62", s: "#FDBB30" },
    { m: ["clippers"], p: "#C8102E", s: "#1D428A" },
    { m: ["lakers"], p: "#552583", s: "#FDB927" },
    { m: ["grizzlies"], p: "#5D76A9", s: "#12173F" },
    { m: ["heat"], p: "#98002E", s: "#F9A01B" },
    { m: ["bucks"], p: "#00471B", s: "#EEE1C6" },
    { m: ["timberwolves", "wolves"], p: "#0C2340", s: "#236192" },
    { m: ["pelicans"], p: "#0C2340", s: "#C8102E" },
    { m: ["knicks"], p: "#006BB6", s: "#F58426" },
    { m: ["thunder"], p: "#007AC1", s: "#EF3B24" },
    { m: ["magic"], p: "#0077C0", s: "#C4CED4" },
    { m: ["76ers", "sixers"], p: "#006BB6", s: "#ED174C" },
    { m: ["suns"], p: "#1D1160", s: "#E56020" },
    { m: ["trail blazers", "blazers"], p: "#E03A3E", s: "#000000" },
    { m: ["kings"], p: "#5A2D81", s: "#63727A" },
    { m: ["spurs"], p: "#C4CED4", s: "#000000" },
    { m: ["raptors"], p: "#CE1141", s: "#000000" },
    { m: ["jazz"], p: "#002B5C", s: "#00471B" },
    { m: ["wizards"], p: "#002B5C", s: "#E31837" },
  ],
  wnba: [
    { m: ["aces"], p: "#BA0C2F", s: "#000000" },
    { m: ["sky"], p: "#418FDE", s: "#FDD023" },
    { m: ["sun"], p: "#F05023", s: "#0A2240" },
    { m: ["wings"], p: "#002B5C", s: "#C4D600" },
    { m: ["fever"], p: "#C8102E", s: "#041E42" },
    { m: ["sparks"], p: "#552583", s: "#FDB927" },
    { m: ["lynx"], p: "#236192", s: "#78BE21" },
    { m: ["liberty"], p: "#6ECEB2", s: "#000000" },
    { m: ["mercury"], p: "#201747", s: "#E56020" },
    { m: ["storm"], p: "#2C5234", s: "#FEE123" },
    { m: ["mystics"], p: "#002B5C", s: "#E03A3E" },
    { m: ["dream"], p: "#C8102E", s: "#418FDE" },
    { m: ["valkyries"], p: "#4B2E83", s: "#BEC0C2" },
  ],
  mlb: [
    { m: ["diamondbacks", "dbacks"], p: "#A71930", s: "#E3D4AD" },
    { m: ["braves"], p: "#CE1141", s: "#13274F" },
    { m: ["orioles"], p: "#DF4601", s: "#000000" },
    { m: ["red sox"], p: "#BD3039", s: "#0C2340" },
    { m: ["white sox"], p: "#27251F", s: "#C4CED4" },
    { m: ["cubs"], p: "#0E3386", s: "#CC3433" },
    { m: ["reds"], p: "#C6011F", s: "#000000" },
    { m: ["guardians"], p: "#00385D", s: "#E31937" },
    { m: ["rockies"], p: "#333366", s: "#C4CED4" },
    { m: ["tigers"], p: "#0C2340", s: "#FA4616" },
    { m: ["astros"], p: "#002D62", s: "#EB6E1F" },
    { m: ["royals"], p: "#004687", s: "#BD9B60" },
    { m: ["angels"], p: "#003263", s: "#BA0021" },
    { m: ["dodgers"], p: "#005A9C", s: "#EF3E42" },
    { m: ["marlins"], p: "#00A3E0", s: "#EF3340" },
    { m: ["brewers"], p: "#12284B", s: "#FFC52F" },
    { m: ["twins"], p: "#002B5C", s: "#D31145" },
    { m: ["yankees"], p: "#0C2340", s: "#C4CED4" },
    { m: ["mets"], p: "#002D72", s: "#FF5910" },
    { m: ["athletics"], p: "#003831", s: "#EFB21E" },
    { m: ["phillies"], p: "#E81828", s: "#002D72" },
    { m: ["pirates"], p: "#FDB827", s: "#27251F" },
    { m: ["padres"], p: "#2F241D", s: "#FFC425" },
    { m: ["giants"], p: "#FD5A1E", s: "#27251F" },
    { m: ["mariners"], p: "#0C2C56", s: "#005C5C" },
    { m: ["cardinals"], p: "#C41E3A", s: "#0C2340" },
    { m: ["rays"], p: "#092C5C", s: "#8FBCE6" },
    { m: ["rangers"], p: "#003278", s: "#C0111F" },
    { m: ["blue jays"], p: "#134A8E", s: "#1D2D5C" },
    { m: ["nationals"], p: "#AB0003", s: "#14225A" },
  ],
  nhl: [
    { m: ["ducks"], p: "#F47A38", s: "#B9975B" },
    { m: ["coyotes"], p: "#8C2633", s: "#E2D6B5" },
    { m: ["mammoth", "utah"], p: "#71AFE5", s: "#010101" },
    { m: ["bruins"], p: "#FFB81C", s: "#000000" },
    { m: ["sabres"], p: "#002654", s: "#FCB514" },
    { m: ["flames"], p: "#C8102E", s: "#F1BE48" },
    { m: ["hurricanes"], p: "#CC0000", s: "#000000" },
    { m: ["blackhawks"], p: "#CF0A2C", s: "#000000" },
    { m: ["avalanche"], p: "#6F263D", s: "#236192" },
    { m: ["blue jackets"], p: "#002654", s: "#CE1126" },
    { m: ["stars"], p: "#006847", s: "#8F8F8C" },
    { m: ["red wings"], p: "#CE1126", s: "#FFFFFF" },
    { m: ["oilers"], p: "#041E42", s: "#FF4C00" },
    { m: ["panthers"], p: "#041E42", s: "#C8102E" },
    { m: ["kings"], p: "#111111", s: "#A2AAAD" },
    { m: ["wild"], p: "#154734", s: "#A6192E" },
    { m: ["canadiens"], p: "#AF1E2D", s: "#192168" },
    { m: ["predators"], p: "#FFB81C", s: "#041E42" },
    { m: ["devils"], p: "#CE1126", s: "#000000" },
    { m: ["islanders"], p: "#00539B", s: "#F47D30" },
    { m: ["rangers"], p: "#0038A8", s: "#CE1126" },
    { m: ["senators"], p: "#C52032", s: "#C2912C" },
    { m: ["flyers"], p: "#F74902", s: "#000000" },
    { m: ["penguins"], p: "#FFB81C", s: "#000000" },
    { m: ["sharks"], p: "#006D75", s: "#EA7200" },
    { m: ["kraken"], p: "#99D9D9", s: "#001628" },
    { m: ["blues"], p: "#002F87", s: "#FCB514" },
    { m: ["lightning"], p: "#002868", s: "#FFFFFF" },
    { m: ["maple leafs", "leafs"], p: "#00205B", s: "#FFFFFF" },
    { m: ["canucks"], p: "#00205B", s: "#00843D" },
    { m: ["golden knights"], p: "#B4975A", s: "#333F42" },
    { m: ["capitals"], p: "#041E42", s: "#C8102E" },
    { m: ["jets"], p: "#041E42", s: "#55565A" },
  ],
};

// deterministic fallback palette (stable per name) for unknown / long-tail teams
const CREST_PALETTE = [
  "#DC2626", "#2563EB", "#059669", "#D97706", "#7C3AED", "#DB2777",
  "#0891B2", "#CA8A04", "#4F46E5", "#B91C1C", "#0D9488", "#9333EA",
  "#EA580C", "#16A34A", "#E11D48", "#1D4ED8",
];
function hashInt(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/* resolveTeamTheme — the canonical resolver. Returns { primary, secondary, text, fallback }.
   `fallback: true` marks a deterministic palette colour (i.e. NOT an authoritative brand colour). */
export function resolveTeamTheme({ team = "", league = "", sport = "", providerColor = "" } = {}) {
  // 1) provider-supplied colour if it looks like a valid hex
  if (providerColor && HEX.test(String(providerColor).trim())) {
    const p = String(providerColor).trim();
    return { primary: p, secondary: "#18181C", text: "#ffffff", fallback: false };
  }
  // 2) canonical mapping within the league group
  const group = groupFor(league, sport);
  const n = normTeam(team);
  if (group && THEMES[group] && n) {
    for (const t of THEMES[group]) {
      if (t.m.some((tok) => n.includes(tok))) {
        return { primary: t.p, secondary: t.s, text: "#ffffff", fallback: false };
      }
    }
  }
  // 3) deterministic fallback palette (stable per name) — unknown / minor teams
  const primary = CREST_PALETTE[hashInt(n || "team") % CREST_PALETTE.length];
  return { primary, secondary: "#18181C", text: "#ffffff", fallback: true };
}

/* teamThemeColor — convenience: just the primary brand colour (drop-in for the old teamColor()). */
export function teamThemeColor({ team = "", league = "", sport = "", providerColor = "" } = {}) {
  return resolveTeamTheme({ team, league, sport, providerColor }).primary;
}

/* hexA — a hex colour at alpha (used for the subtle matchup washes). */
export function hexA(c, a) {
  const h = String(c || "#3F3F46").replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((x) => x + x).join("") : h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

/* matchupWash — a subtle premium left/right wash for a two-team matchup header:
   away primary on the left → dark neutral centre → home primary on the right. */
export function matchupWash(event = {}, alpha = 0.24, { shape = "scoreboard" } = {}) {
  const away = teamThemeColor({ team: event.away_team, league: event.league, sport: event.sport });
  const home = teamThemeColor({ team: event.home_team, league: event.league, sport: event.sport });
  const geo = shape === "card"
    ? ["70% 90% at 0% 0%", "70% 90% at 100% 0%", 0.7]
    : ["60% 140% at 0% 50%", "60% 140% at 100% 50%", 0.72];
  return `radial-gradient(${geo[0]}, ${hexA(away, alpha)} 0%, transparent ${geo[2] * 100}%), radial-gradient(${geo[1]}, ${hexA(home, alpha)} 0%, transparent ${geo[2] * 100}%)`;
}
